import { FormContext } from "common/components/Form/FormContext";
import React, { useContext } from "react";
import { Nullable } from "common/types";
import { FieldLabel } from "common/components/FieldLabel";
import { FormControlData } from "common/components/Form/Data/FormControlData";
import { IFieldBaseProps } from "common/components/Field";
import { P } from "common/components/Paragraph";
import { Tooltip } from "common/components/Tooltip";
import { useLocation } from "react-router";

export interface IFormControlProps extends IFieldBaseProps<"inputText" | "inputNumber" | "textarea" | "range"> {
  /** Short hint that describes the expected value of an input field */
  placeholder?: string;
  /** Optional input validator */
  validate?: (value: string, rootRecord?: any) => Nullable<string>;
  /** The input element id. If undefined, "name" will be used */
  id?: string;
  /** If the input field should be disabled */
  disabled?: boolean;
  /** Focus on the input upon load */
  autoFocus?: boolean;
  /** The name of the icon to be rendered before the text */
  icon?: string;
  /** Optional HTML input type */
  htmlType?: string;
  /** Optional number of decimal places to be rounded, defaults to 2 */
  precision?: number;
  /** Change value by this amount when using up/down */
  step?: number;
  /** Optional minimum possible value */
  minValue?: number;
  /** Optional maximum possible value */
  maxValue?: number;
  /** Optional callback function to be called on input */
  onChange?: (value: string) => void;
  /** Optional prefix to add to input */
  prefix?: React.ReactNode;
  /** Optional value if we want to show a read only value (input is automatically disabled) */
  readOnlyValue?: number | string;
  /** Optional value to hide field */
  hidden?: boolean;
  /** Optional description rendered in an info tooltip inline with the field label */
  tooltipDescription?: string;
  /**
   * Accessible name for the input when no visible `label` is rendered (e.g. a dense table cell whose
   * column header already names it). Falls back to `label` when omitted, so existing callers are
   * unaffected.
   */
  ariaLabel?: string;
}

const disabledStyles = "bg-secondary-50 !text-secondary-300 !cursor-not-allowed [&_input]:cursor-not-allowed";

export const FormControl: React.FC<IFormControlProps> = ({
  label = "",
  name,
  type,
  placeholder,
  validate,
  id,
  disabled,
  icon,
  autoFocus,
  htmlType,
  minValue,
  maxValue,
  precision = 2,
  step,
  onChange,
  prefix,
  readOnlyValue,
  hidden,
  tooltipDescription,
  ariaLabel,
}) => {
  const { hash } = useLocation();
  const secondHash = hash?.split("#")[2];
  const isHash = secondHash?.replaceAll("-", " ") === label.toLowerCase();
  // If the caller provided a read-only value, make the field read-only
  if (readOnlyValue !== undefined) {
    disabled = true;
  }

  const { errors } = useContext(FormContext);
  const nameId = id || name;
  const inputNumberSpecificClassName =
    "[&_input]:bg-transparent [&_input]:outline-none [&_input]:w-full [&_input]:p-0 [&_input]:border-none [&_input]:text-sm [&_input]:leading-4";
  let ownClassName =
    "border border-secondary-100 text-sm text-secondary-900 w-full placeholder:text-secondary-500 rounded-3xl p-sm focus:outline-none cursor-text flex items-center";

  ownClassName = ownClassName.concat(" ", inputNumberSpecificClassName);

  if (disabled) {
    ownClassName = ownClassName.concat(" ", disabledStyles);
  }

  if (icon) {
    ownClassName = ownClassName.concat(" ", " pl-2xl");
  }

  if (hidden) {
    ownClassName = ownClassName.concat(" ", " hidden");
  }

  if (type !== "textarea") {
    ownClassName = ownClassName.concat(" ", " h-[36px]");
  }

  if (type === "inputNumber" && !disabled) {
    ownClassName = ownClassName.concat(" ", " bg-white");
  }

  // input number focus
  ownClassName = ownClassName.concat(
    " ",
    "focus-within:border-secondary-200 focus-within:ring focus-within:ring-[3px] focus-within:ring-secondary-50",
  );

  if (errors.get(name)) {
    ownClassName = ownClassName.concat(
      " ",
      "!border-error-400 focus-within:!border-error-400 focus-within:!ring-error-50",
    );
  }

  return (
    <div className={`flex flex-col gap-xs w-full ${isHash ? "bg-primary-50 p-2" : ""}`}>
      <label className="flex flex-col gap-xs w-full" htmlFor={nameId}>
        {label && (
          <div className="flex items-center gap-xs">
            <FieldLabel>{label}</FieldLabel>
            {tooltipDescription && (
              <Tooltip description={tooltipDescription} size="sm">
                <span className="material-symbols-outlined text-secondary-500 text-xl">info</span>
              </Tooltip>
            )}
          </div>
        )}
        <div className="flex items-center relative">
          {icon && <span className="material-symbols-outlined absolute text-secondary-500 text-lg ml-sm">{icon}</span>}
          <FormControlData
            label={label}
            ariaLabel={ariaLabel}
            name={name}
            id={nameId}
            className={ownClassName}
            type={type}
            htmlType={htmlType}
            placeholder={placeholder}
            validate={validate}
            disabled={disabled}
            autoFocus={isHash || autoFocus}
            precision={precision}
            step={step}
            minValue={minValue}
            maxValue={maxValue}
            onChange={onChange}
            prefix={prefix}
            readOnlyValue={readOnlyValue}
          />
          {errors.get(name) && !hidden && type !== "range" && (
            <span className="material-symbols-outlined relative text-error-400 text-lg -ml-xl">{"error_outline"}</span>
          )}
        </div>
      </label>
      {errors.get(name) && !hidden && (
        <P className="text-error-400 leading-[16px]" size="sm">
          {errors.get(name)}
        </P>
      )}
    </div>
  );
};
